import { useEffect, useRef, useState } from "react";
import { LuCamera, LuImage, LuPaperclip } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useProjectId } from "../../../core_stores/useProjectStore";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { usePromptInputActions } from "../../../view_stores/usePromptInputStore";
import { useProjectPreviewActions } from "../../../view_stores/useProjectPreviewStore";
import { components } from "../../../design/tokens";
import { ScreenshotCropModal } from "../../common_components/ScreenshotCropModal";
import { shouldHideScreenshot } from "../../../utils/browserUtils";

export function ProjectPreviewScreenshotControls({
  className,
  optionsPosition = "top-left",
}: {
  className?: string;
  optionsPosition?: "top-left" | "bottom-center";
}) {
  const projectId = useProjectId();
  const isCompactView = useIsCompactView();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const { showSnackbar } = useSnackbar();
  const { generateScreenshot } = useProjectPreviewActions();
  const { setAttachedImage } = usePromptInputActions();

  const [isHovered, setIsHovered] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsHovered(false), 300);
  };

  const saveScreenshot = async () => {
    try {
      const canvas = await generateScreenshot();
      console.log("canvas", canvas);
      if (!canvas) {
        showSnackbar("Failed to capture screenshot", {
          type: "error",
        });
        return;
      }

      const dataUrl = canvas.toDataURL("image/jpeg");
      const link = document.createElement("a");
      link.download = `${projectId}-screenshot.jpg`;
      link.href = dataUrl;
      link.click();
      showSnackbar("Screenshot saved to disk");
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
    }
  };

  const attachScreenshot = async () => {
    try {
      const canvas = await generateScreenshot();
      if (!canvas) {
        showSnackbar("Failed to capture screenshot", {
          type: "error",
        });
        return;
      }

      // On mobile: directly attach full screenshot (no modal)
      if (isCompactView) {
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Failed to create blob"));
          }, "image/png");
        });
        const file = new File([blob], `${projectId}-screenshot.png`, { type: "image/png" });
        setAttachedImage(file);
        showSnackbar("Screenshot added to prompt");
        setIsHovered(false);
        return;
      }

      // On desktop: open crop modal
      const dataUrl = canvas.toDataURL("image/png");
      setScreenshotDataUrl(dataUrl);
      setIsCropModalOpen(true);
      setIsHovered(false);
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
    }
  };

  const handleCropConfirm = (file: File) => {
    setAttachedImage(file);
    showSnackbar("Screenshot added to prompt");
  };

  const handleCropModalClose = () => {
    setIsCropModalOpen(false);
    setScreenshotDataUrl(null);
  };

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  // Hide screenshot feature on Safari and Firefox due to html2canvas limitations
  if (shouldHideScreenshot()) {
    return null;
  }

  return (
    <>
      <div className={className}>
        <div
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className="text-dark-text-muted hover:text-dark-text
            p-[10px] rounded-full shadow-lg
          hover:bg-dark-surface-secondary
          transition-colors
          flex items-center justify-center"
            onClick={() => setIsHovered(!isHovered)}
          >
            <LuCamera size={20} />
          </button>
          <div
            className={`absolute ${
              optionsPosition === "bottom-center"
                ? "top-full mt-2 left-1/2 -translate-x-1/2"
                : "bottom-full mb-2 right-0"
            }  z-40 transition-all duration-300 ${
              isHovered
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
          >
            <div className={components.dropdown.container}>
              <Btn
                icon={<LuImage size={20} />}
                label="Save to disk"
                onClick={saveScreenshot}
              />
              <Btn
                icon={<LuPaperclip size={20} />}
                label="Add to prompt"
                onClick={attachScreenshot}
              />
            </div>
          </div>
        </div>
      </div>

      {screenshotDataUrl && projectId && (
        <ScreenshotCropModal
          isOpen={isCropModalOpen}
          onClose={handleCropModalClose}
          imageDataUrl={screenshotDataUrl}
          projectId={projectId}
          onConfirm={handleCropConfirm}
        />
      )}
    </>
  );
}

function Btn({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={components.dropdown.item}
      role="menuitem"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
